import type { Calendar as EventCalendar } from '@event-calendar/core'
import {
  filter,
  isArray,
  isBoolean,
  isDefined,
  isNumber,
  isPlainObject,
  isString,
  map,
  pipe
} from 'remeda'
import { type Cleanup, composeCleanup } from '../helpers/cleanup'
import { createIcon } from '../helpers/icon'
import { pushAndCall, pushEvent, type ViewHook } from '../helpers/utils'

// Private types ---------------------------------------------------------------

type CalendarHook = ViewHook & {
  calendarAbort?: AbortController
  calendarState?: CalendarState
  handleEvent: (event: string, callback: (payload: unknown) => void) => unknown
  removeHandleEvent: (callbackRef: unknown) => void
}

type CalendarState = Readonly<{
  destroy: () => Promise<void>
}>

type CalendarConfig = Readonly<{
  addButtonEnabled: boolean
  addButtonLabel: string
  dragCreateEnabled: boolean
  editable: boolean
  initialDate?: string
  viewSwitcherEnabled: boolean
  events: Readonly<{
    dataRequested?: string
    dateClick?: string
    eventClick?: string
    eventDrop?: string
    eventResize?: string
    eventSelect?: string
  }>
  target?: string
}>

type CalendarPush = (
  event: string,
  payload: Readonly<Record<string, unknown>>,
  onReply?: (reply: unknown) => void
) => void

type DateFormatter = (date: Date) => string

type CalendarDependencies = Readonly<{
  calendarModule: typeof import('@event-calendar/core')
  formatDate: DateFormatter
  today: string
}>

type CalendarTask = Readonly<Record<string, unknown>>

type CalendarOptions = EventCalendar.Options &
  Readonly<{
    eventMaxStack?: number
    expandRows?: boolean
    stickyHeaderDates?: boolean
  }>

// Constants -------------------------------------------------------------------

const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/

const CALENDAR_REFETCH_EVENT = 'calendar:refetch'
const DAY_IN_MILLISECONDS = 86_400_000
const TIME_FORMAT = {
  hour: '2-digit',
  hour12: false,
  minute: '2-digit'
} as const satisfies Intl.DateTimeFormatOptions

// Private functions -----------------------------------------------------------

// Reads the hook's server-provided behavior without retaining its live dataset.
const readCalendarConfig = (element: HTMLElement): CalendarConfig => ({
  addButtonEnabled: element.dataset.addButtonEnabled === '',
  addButtonLabel: element.dataset.addButtonLabel ?? '',
  dragCreateEnabled: element.dataset.dragCreateEnabled === '',
  editable: element.dataset.editable === '',
  initialDate: ISO_DATE_PATTERN.test(element.dataset.initialDate ?? '')
    ? element.dataset.initialDate
    : undefined,
  events: {
    dataRequested: element.dataset.onDataRequested,
    dateClick: element.dataset.onDateClick,
    eventClick: element.dataset.onEventClick,
    eventDrop: element.dataset.onEventDrop,
    eventResize: element.dataset.onEventResize,
    eventSelect: element.dataset.onEventSelect
  },
  target: element.getAttribute('phx-target') ?? undefined,
  viewSwitcherEnabled: element.dataset.viewSwitcherEnabled === ''
})

// Loads optional calendar code together and configures the shared date adapter.
const loadCalendarDependencies = async (): Promise<CalendarDependencies> => {
  const [dayjs, , utc, calendarModule] = await Promise.all([
    import('dayjs'),
    import('dayjs/locale/uk'),
    import('dayjs/plugin/utc'),
    import('@event-calendar/core')
  ])

  dayjs.default.locale('uk')
  dayjs.default.extend(utc.default)

  return {
    calendarModule,
    formatDate: (date) => dayjs.default.utc(date).local().format(),
    today: dayjs.default.utc(new Date()).local().format()
  }
}

// Sends one configured event through the hook's existing error boundary.
const createCalendarPush =
  (hook: CalendarHook, target?: string): CalendarPush =>
  (event, payload, onReply) => {
    if (onReply) {
      pushAndCall<unknown>(
        hook,
        event,
        payload,
        onReply,
        target,
        'Calendar push with callback failed'
      )
      return
    }

    pushEvent(hook, event, payload, target, 'Calendar push failed')
  }

// Converts an exclusive all-day range into inclusive noon-based form defaults.
const normalizeAllDayRange = (
  info: Pick<EventCalendar.SelectInfo, 'allDay' | 'endStr' | 'startStr'>
): Readonly<{ start: string; end: string }> => {
  if (!info.allDay) {
    return { start: info.startStr, end: info.endStr }
  }

  const startDay = info.startStr.slice(0, 10)
  const exclusiveEnd = new Date(`${info.endStr.slice(0, 10)}T00:00:00Z`)
  const endDay = new Date(exclusiveEnd.getTime() - DAY_IN_MILLISECONDS)
    .toISOString()
    .slice(0, 10)

  return {
    start: `${startDay}T12:00:00`,
    end: `${endDay}T12:00:00`
  }
}

// Reads a string property from an untrusted server event.
const readString = (
  value: CalendarTask,
  property: string
): string | undefined => {
  const candidate = value[property]

  return isString(candidate) ? candidate : undefined
}

// Reads an event id while rejecting non-finite numeric values.
const readEventId = (value: CalendarTask): EventCalendar.EventInput['id'] => {
  const candidate = value.id

  return isString(candidate) ||
    (isNumber(candidate) && Number.isFinite(candidate))
    ? candidate
    : undefined
}

// Normalizes one server task into Event Calendar's trusted event input.
const readCalendarEvent = (
  task: CalendarTask
): EventCalendar.EventInput | undefined => {
  const start = readString(task, 'start') ?? readString(task, 'startDate')

  if (start === undefined) {
    return undefined
  }

  const end = readString(task, 'end') ?? readString(task, 'endDate') ?? start
  const id = readEventId(task)
  const title = readString(task, 'title') ?? readString(task, 'name')
  const allDay = task.allDay
  const extendedProps = task.extendedProps
  const backgroundColor = readString(task, 'backgroundColor')
  const textColor = readString(task, 'textColor')
  const color = readString(task, 'color')

  return {
    start,
    end,
    ...(id === undefined ? {} : { id }),
    ...(title === undefined ? {} : { title }),
    ...(isBoolean(allDay) ? { allDay } : {}),
    ...(backgroundColor === undefined ? {} : { backgroundColor }),
    ...(textColor === undefined ? {} : { textColor }),
    ...(color === undefined ? {} : { color }),
    ...(isPlainObject(extendedProps) ? { extendedProps } : {})
  }
}

// Validates a server reply once and returns only renderable calendar events.
const readCalendarEvents = (
  reply: unknown
): readonly EventCalendar.EventInput[] => {
  if (!(isPlainObject(reply) && isArray(reply.tasks))) {
    return []
  }

  return pipe(
    reply.tasks,
    filter(isPlainObject),
    map(readCalendarEvent),
    filter(isDefined)
  )
}

// Builds the optional add action shown inside a calendar day cell.
const createDayCellContent = (
  config: CalendarConfig,
  push: CalendarPush
): EventCalendar.Options['dayCellContent'] => {
  const selectEvent = config.events.eventSelect

  if (!(config.addButtonEnabled && selectEvent)) {
    return undefined
  }

  return (info) => {
    const wrapper = document.createElement('div')
    const dayNumber = document.createElement('span')
    const button = document.createElement('button')
    const content = document.createElement('span')
    const icon = createIcon('plus', 'regular', 'xs')

    wrapper.className = 'ec-day-cell-content'
    dayNumber.className = 'ec-day-cell-number'
    dayNumber.textContent = String(info.date.getDate())

    button.type = 'button'
    button.className = 'ec-day-cell-add-button'
    button.dataset.part = 'event-content-wrapper'
    button.setAttribute('aria-label', config.addButtonLabel || '+')
    button.style.setProperty('--bg', 'var(--bg-gray)')
    button.style.setProperty('--text', 'var(--text-gray)')
    button.style.setProperty('--border', 'var(--border-gray)')

    content.dataset.part = 'event-content'
    icon.dataset.part = 'event-icon'
    content.append(icon)

    if (config.addButtonLabel !== '') {
      const label = document.createElement('span')

      label.textContent = config.addButtonLabel
      content.append(label)
    }

    const stopSelection = (event: Event): void => event.stopPropagation()
    const pushSelection = (event: Event): void => {
      event.stopPropagation()
      event.preventDefault()

      const date = [
        info.date.getFullYear(),
        String(info.date.getMonth() + 1).padStart(2, '0'),
        String(info.date.getDate()).padStart(2, '0')
      ].join('-')

      push(selectEvent, {
        data: {
          start: `${date}T12:00:00`,
          end: `${date}T12:00:00`
        }
      })
    }

    button.append(content)
    button.addEventListener('pointerdown', stopSelection)
    button.addEventListener('mousedown', stopSelection)
    button.addEventListener('click', pushSelection)
    wrapper.append(dayNumber, button)

    return { domNodes: [wrapper] }
  }
}

// Appends a compact local-time range to one rendered event.
const appendEventTime = (
  content: HTMLElement,
  event: EventCalendar.Event
): void => {
  if (event.allDay) {
    return
  }

  const wrapper = document.createElement('div')
  const start = document.createElement('time')
  const divider = document.createElement('span')
  const end = document.createElement('time')

  wrapper.dataset.part = 'event-time'
  start.dataset.part = 'event-time-start'
  start.textContent = event.start.toLocaleTimeString([], TIME_FORMAT)
  divider.dataset.part = 'event-time-divider'
  divider.textContent = '->'
  end.dataset.part = 'event-time-end'
  end.textContent = event.end.toLocaleTimeString([], TIME_FORMAT)
  wrapper.append(start, divider, end)
  content.append(wrapper)
}

// Appends the optional "of which" annotation: a subset of the chip's own figure,
// so it is separated and muted rather than reading as a second amount.
const appendEventSecondary = (
  wrapper: HTMLElement,
  event: EventCalendar.Event
): void => {
  const secondary = event.extendedProps.secondary

  if (!isString(secondary)) {
    return
  }

  const aside = document.createElement('span')
  const iconName = event.extendedProps.secondary_icon

  aside.dataset.part = 'event-secondary'

  if (isString(iconName)) {
    const icon = createIcon(iconName, 'regular', 'xs')

    icon.dataset.part = 'event-secondary-icon'
    aside.append(icon)
  }

  const amount = document.createElement('span')

  amount.textContent = secondary
  aside.append(amount)
  wrapper.append(aside)
}

// Renders one event chip from validated Event Calendar data.
const createEventContent = (
  info: EventCalendar.EventContentInfo
): EventCalendar.Content => {
  const content = document.createElement('span')
  const wrapper = document.createElement('span')
  const title = document.createElement('span')
  const color = info.event.extendedProps.color
  const iconName = info.event.extendedProps.icon
  const titleText = isString(info.event.title) ? info.event.title : ''
  const secondaryText = info.event.extendedProps.secondary

  wrapper.dataset.part = 'event-content-wrapper'
  content.dataset.part = 'event-content'
  content.title = isString(secondaryText)
    ? `${titleText} (${secondaryText})`
    : titleText
  title.textContent = titleText

  if (isString(color)) {
    wrapper.style.setProperty('--border', `var(--border-${color})`)
    wrapper.style.setProperty('--text', `var(--text-${color})`)
  }

  if (isString(iconName)) {
    const icon = createIcon(iconName, 'regular', 'xs')

    icon.dataset.part = 'event-icon'
    content.append(icon)
  }

  appendEventTime(content, info.event)
  content.append(title)
  wrapper.append(content)
  appendEventSecondary(wrapper, info.event)

  return { domNodes: [wrapper] }
}

// Builds the optional server-backed event source.
const createEventSources = (
  hook: CalendarHook,
  config: CalendarConfig,
  push: CalendarPush
): EventCalendar.EventSource[] => {
  const dataRequestedEvent = config.events.dataRequested

  if (dataRequestedEvent === undefined) {
    return []
  }

  return [
    {
      events: (fetchInfo, success) =>
        push(dataRequestedEvent, { id: hook.el.id, data: fetchInfo }, (reply) =>
          success([...readCalendarEvents(reply)])
        )
    }
  ]
}

// Formats the same event range for both drag and resize notifications.
const eventRangePayload = (
  event: Readonly<Pick<EventCalendar.Event, 'id' | 'start' | 'end'>>,
  formatDate: DateFormatter
): {
  readonly id: number | string
  readonly data: { readonly start: string; readonly end: string }
} => ({
  id: event.id,
  data: {
    start: formatDate(event.start),
    end: formatDate(event.end)
  }
})

// Builds Event Calendar options from immutable hook configuration.
const createCalendarOptions = (
  hook: CalendarHook,
  config: CalendarConfig,
  dependencies: CalendarDependencies,
  push: CalendarPush
): CalendarOptions => ({
  editable: config.editable,
  firstDay: 1,
  view: 'dayGridMonth',
  height: '100%',
  locale: 'uk-UA',
  date: config.initialDate ?? dependencies.today,
  selectable: config.dragCreateEnabled,
  eventMaxStack: 5,
  nowIndicator: true,
  dayMaxEvents: true,
  stickyHeaderDates: false,
  expandRows: true,
  slotMinTime: '08:00:00',
  slotMaxTime: '20:00:00',
  slotDuration: '00:15:00',
  selectBackgroundColor: 'var(--color-brand-4)',
  buttonText: {
    today: 'Сьогодні',
    dayGridMonth: 'Місяць',
    listDay: 'Порядок денний',
    listWeek: 'Порядок денний',
    listMonth: 'Порядок денний',
    listYear: 'Порядок денний',
    resourceTimeGridDay: 'День',
    resourceTimeGridWeek: 'Тиждень',
    timeGridDay: 'День',
    timeGridWeek: 'Тиждень'
  },
  headerToolbar: {
    start: 'prev,next today',
    center: 'title',
    end: config.viewSwitcherEnabled
      ? 'dayGridMonth,timeGridWeek,timeGridDay,listMonth'
      : ''
  },
  moreLinkContent: ({ num }) => `+ щє ${num}`,
  dayCellContent: createDayCellContent(config, push),
  noEventsContent: () => 'Heмає подій',
  allDayContent: 'Увесь день',
  eventClick: (info) => {
    const eventName = config.events.eventClick

    if (eventName === undefined) {
      return
    }

    const bucketDate = info.event.extendedProps.bucket_date
    const bucketStatusId = info.event.extendedProps.bucket_status_id

    push(
      eventName,
      isString(bucketDate)
        ? {
            date: bucketDate,
            status_id: isString(bucketStatusId) ? bucketStatusId : null
          }
        : { id: info.event.id }
    )
  },
  dateClick: (info) => {
    const eventName = config.events.dateClick

    if (eventName !== undefined) {
      push(eventName, { date: info.dateStr.slice(0, 10) })
    }
  },
  eventResize: (info) => {
    const eventName = config.events.eventResize

    if (eventName !== undefined) {
      push(eventName, eventRangePayload(info.event, dependencies.formatDate))
    }
  },
  eventDrop: (info) => {
    const eventName = config.events.eventDrop

    if (eventName !== undefined) {
      push(eventName, eventRangePayload(info.event, dependencies.formatDate))
    }
  },
  select: (info) => {
    const eventName = config.events.eventSelect

    if (eventName !== undefined && config.dragCreateEnabled) {
      push(eventName, { data: normalizeAllDayRange(info) })
    }
  },
  eventContent: createEventContent,
  eventSources: createEventSources(hook, config, push)
})

// Extracts a refetch target from either a DOM target or CustomEvent detail.
const readRefetchId = (value: unknown): string | undefined => {
  if (value instanceof Event && value.target instanceof HTMLElement) {
    return value.target.id
  }

  if (
    value instanceof CustomEvent &&
    isPlainObject(value.detail) &&
    isString(value.detail.id)
  ) {
    return value.detail.id
  }

  return isPlainObject(value) && isString(value.id) ? value.id : undefined
}

// Connects both browser and LiveView refetch channels with paired cleanup.
const setupRefetch = (hook: CalendarHook, calendar: EventCalendar): Cleanup => {
  const refetch = (value: unknown): void => {
    if (readRefetchId(value) === hook.el.id) {
      calendar.refetchEvents()
    }
  }
  const listener: EventListener = (event) => refetch(event)
  const callbackRef = hook.handleEvent(CALENDAR_REFETCH_EVENT, refetch)

  window.addEventListener(CALENDAR_REFETCH_EVENT, listener)

  return composeCleanup(
    () => window.removeEventListener(CALENDAR_REFETCH_EVENT, listener),
    () => hook.removeHandleEvent(callbackRef)
  )
}

// Mounts one calendar and returns the complete lifecycle boundary.
const setupCalendar = async (
  hook: CalendarHook,
  signal: AbortSignal
): Promise<CalendarState | undefined> => {
  const dependencies = await loadCalendarDependencies()

  if (signal.aborted) {
    return undefined
  }

  const config = readCalendarConfig(hook.el)
  const push = createCalendarPush(hook, config.target)
  const {
    createCalendar,
    destroyCalendar,
    DayGrid,
    Interaction,
    List,
    TimeGrid
  } = dependencies.calendarModule
  const calendar = createCalendar(
    hook.el,
    [DayGrid, TimeGrid, List, Interaction],
    createCalendarOptions(hook, config, dependencies, push)
  )
  const cleanup = setupRefetch(hook, calendar)

  return {
    destroy: async () => {
      cleanup()
      await destroyCalendar(calendar)
    }
  }
}

// Shows the existing graceful fallback when optional calendar code cannot load.
const renderUnavailable = (element: HTMLElement): void => {
  const fallback = document.createElement('div')

  fallback.className = 'p-4 text-center text-gray-500'
  fallback.textContent = 'Calendar functionality not available'
  element.replaceChildren(fallback)
}

// Public hook -----------------------------------------------------------------

export const FacadeCalendar = {
  // Starts calendar loading and prevents late setup after hook destruction.
  async mounted(this: CalendarHook): Promise<void> {
    const controller = new AbortController()

    this.calendarAbort = controller

    try {
      this.calendarState = await setupCalendar(this, controller.signal)
    } catch {
      if (!controller.signal.aborted) {
        renderUnavailable(this.el)
      }
    }
  },

  // Cancels pending setup and releases the mounted calendar instance.
  async destroyed(this: CalendarHook): Promise<void> {
    this.calendarAbort?.abort()

    const state = this.calendarState

    this.calendarAbort = undefined
    this.calendarState = undefined

    await state?.destroy()
  }
}
